import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import styled from 'styled-components'
import _ from 'lodash'
import { Button } from 'wix-style-react'
import { Popover } from '@shopify/polaris'
import DeleteIcon from 'react-icons/lib/md/delete'

import Text from 'components/common/Text'
import { uploadToStorage } from 'controllers/storage'
import ImageCrop from './ImageCrop'
import Card from 'components/shared/Card'
import { BLUE, LIGHTEST_GRAY, PURPLE } from 'components/styles'
import { getReadableFileSizeString, formatMillisecondsDate } from 'utils/editorUtils'

const Image = styled.img`
  max-width: 100%;
  max-height: 100%;
  vertical-align: middle;
`
const VideoContainer = styled.div`
  position: absolute;
  top: 3%;
  left: 6%;
  right: 6%;
  bottom: 37%;
  background-color: ${LIGHTEST_GRAY};
  overflow: hidden;
`
const Video = styled.video`
  height: 100%;
  object-fit: cover;
`
const Title = styled.div`
  position: absolute;
  text-align: center;
  line-height: 0.5;
  width: 100%;
  bottom: 5px;
  background-color: rgba(234, 247, 255, 0.59);
  word-wrap: break-word;
`
const AssetInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1em;
`
const IconSelected = styled.div`
  position: absolute;
  top: 40%;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  color: ${BLUE};
`
const SelectedTextContainer = styled.div`
  background-color: rgba(251, 251, 251, 0.9);
  padding: 3px 8px 3px;
  border-radius: 5px;
  color: #000;
`

const OverlayContainer = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  _background-color: rgba(242, 246, 248, 0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`
const AdditionalButtonsContainer = styled.div`
  position: absolute;
  bottom: 0.5em;
  left: 0.5em;
  right: 0.5em;
  height: 2em;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`


class AssetCard extends Component {

  constructor (props) {
    super(props)
    this.state = {
      overlayVisible: false,
      file: null,
      tooltipVisible: false,
      tooltipNeeded: this.isTooltipNeeded(props)
    }
  }

  isTooltipNeeded (props) {
    const { asset } = props
    let res = _.has(asset, 'name')
    res = res || _.has(asset, 'width')
    res = res || _.has(asset, 'height')
    res = res || _.has(asset, 'uploadedAt')
    res = res && !_.isNil(asset.url)
    return res
  }

  componentWillReceiveProps (nextProps) {
    this.setState({
      tooltipNeeded: this.isTooltipNeeded(nextProps)
    })
  }

  renderCardContent () {
    const { asset } = this.props
    if (asset.type === 'video') {
      return (
        <VideoContainer>
          <Video controls={false}>
            <source src={asset.url} />
          </Video>
        </VideoContainer>
      )
    } else if (asset.url) {
      return (
        <Image src={asset.url} role={'presentation'} />
      )
    } else {
      return (
        <Text>None</Text>
      )
    }
  }

  onDelete = (e) => {
    // stop propagation to prevent interference with selection
    e.stopPropagation()
    // console.log('call on delete', this.props.asset)
    this.props.onDelete(this.props.asset)
  }

  onCropComplete = (file) => {
    const { dispatch, appId } = this.props
    this.setState({
      file: null
    })
    if (_.isObject(file)) {
      dispatch(uploadToStorage(appId, file))
    }
  }

  renderCropModal = () => {
    const { file, assetInfo } = this.state
    if (!_.isNil(file)) {
      return (
        <ImageCrop
          file={file}
          assetInfo={assetInfo}
          onComplete={this.onCropComplete}
        />
      )
    }
  }

  runCrop = () => {
    this.setState({
      file: this.props.asset.url,
      assetInfo: this.props.asset
    })
  }

  renderOverlayAdditionalButtons () {
    return (
      <AdditionalButtonsContainer>
        {this.props.onDelete &&
          <Button theme='icon-standard' onClick={this.onDelete}>
            <DeleteIcon size='24' />
          </Button>
        }
      </AdditionalButtonsContainer>
    )
  }

  renderOverlay = () => {
    if (this.state.overlayVisible) {
      return (
        <OverlayContainer>
          {this.renderOverlayAdditionalButtons()}
        </OverlayContainer>
      )
    }
  }

  renderAssetInfo = () => {
    const { asset } = this.props
    const info = (asset.width && asset.height)
      ? `${asset.width}x${asset.height}, ${getReadableFileSizeString(asset.size)}`
      : null
    return (
      <AssetInfoContainer>
        <Text size='medium'>{asset.name}</Text>
        {!_.isNil(info) && <Text size='medium' variation='subdued'>{_.upperFirst(_.get(asset, 'type', 'image'))}: {info}</Text>}
        <Text size='medium' variation='subdued'>{formatMillisecondsDate(asset.uploadedAt)}</Text>
      </AssetInfoContainer>
    )

  }

  handleMouseEnter = () => {
    this.setState({overlayVisible: true})
    this.setState({tooltipVisible: true})
  }

  handleMouseLeave = () => {
    this.setState({overlayVisible: false})
    this.setState({tooltipVisible: false})
  }

  renderSelected = () => {
    const { asset } = this.props
    if (asset.url) {
      return (
        <IconSelected>
          <SelectedTextContainer>
            <Text>Selected</Text>
            {/* <CheckCircle style={{backgroundColor: 'white', borderRadius: 12, width: 24, height: 24}} /> */}
          </SelectedTextContainer>
        </IconSelected>
      )
    }
  }

  renderCard () {
    const { isSelected, asset } = this.props
    const name = asset.name
    const eventListeners = {
      onMouseEnter: this.handleMouseEnter,
      onMouseLeave: this.handleMouseLeave
    }
    let styleRaw = ''
    if (_.isNil(asset.url)) styleRaw = styleRaw + `background-color: white;`
    if (isSelected) {
      styleRaw = styleRaw + `border: 2px ${PURPLE} solid;`
    } else if (_.isNil(asset.url)) {
      styleRaw = styleRaw + 'border: 1px gray solid;'
    }


    return (
      <Card
        eventListeners={eventListeners}
        pointer
        onClick={this.props.onSet}
        styleRaw={styleRaw}
      >
        {this.renderCardContent()}
        {isSelected && this.renderSelected()}
        <Title>
          <Text size='small'>{name}</Text>
        </Title>
        {this.renderOverlay()}
        {this.renderCropModal()}
      </Card>
    )
  }

  render () {
    // console.log('render asset', this.props.isSelected)
    const { tooltipNeeded } = this.state
    if (tooltipNeeded) {
      return (
        <Popover
          preventAutofocus
          active={this.state.tooltipVisible}
          activator={this.renderCard()}
          preferredPosition='above'
          onClose={() => null}
        >
          {this.renderAssetInfo()}
        </Popover>
      )
    } else {
      return this.renderCard()
    }
  }
}

AssetCard.propTypes = {
  asset: PropTypes.object.isRequired,
  appId: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  isSelected: PropTypes.bool,
  onSet: PropTypes.func,
}

export default connect()(AssetCard)



// WEBPACK FOOTER //
// ./src/components/editor/assetsManager/AssetCard.js